// Push Notification Service
// Handles browser push notifications

class PushNotificationService {
  constructor() {
    this.permission = 'default';
    this.supported = 'Notification' in window;
  }

  // Check if notifications are supported
  isSupported() {
    return this.supported;
  }

  // Get current permission status
  getPermission() {
    if (!this.supported) return 'unsupported';
    return Notification.permission;
  }

  // Request permission from user
  async requestPermission() {
    if (!this.supported) {
      console.warn('Push notifications not supported');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Show a push notification
  show(title, options = {}) {
    if (!this.supported) return null;
    if (Notification.permission !== 'granted') return null;

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      silent: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);

      // Handle click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        if (options.onClick) {
          options.onClick(event);
        }
        notification.close();
      };

      // Auto close after timeout
      if (options.autoClose !== false) {
        setTimeout(() => {
          notification.close();
        }, options.timeout || 5000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  // Predefined notification types
  showDeposit(amount, currency = 'USDT') {
    return this.show('💰 Депозит одобрен', {
      body: `+${amount} ${currency} зачислено на ваш счёт`,
      tag: 'deposit',
      icon: '/logo192.png',
    });
  }

  showWithdrawal(amount, currency = 'USDT', status = 'approved') {
    const title = status === 'approved' ? '✅ Вывод обработан' : '❌ Вывод отклонён';
    return this.show(title, {
      body: status === 'approved' 
        ? `${amount} ${currency} отправлено на ваш кошелёк`
        : `Заявка на вывод ${amount} ${currency} отклонена`,
      tag: 'withdrawal',
    });
  }

  showTradeAlert(symbol, side, pnl) {
    const isProfit = pnl >= 0;
    return this.show(isProfit ? '📈 Позиция закрыта с прибылью' : '📉 Позиция закрыта', {
      body: `${symbol} ${side.toUpperCase()}: ${isProfit ? '+' : ''}$${pnl.toFixed(2)}`,
      tag: 'trade',
    });
  }

  showPriceAlert(symbol, price, direction) {
    return this.show(`⚡ ${symbol} Price Alert`, {
      body: `${symbol} ${direction === 'up' ? '↑' : '↓'} $${price.toLocaleString()}`,
      tag: 'price-alert',
    });
  }

  showTakeProfitHit(symbol, pnl) {
    return this.show('🎯 Take Profit сработал!', {
      body: `${symbol}: +$${pnl.toFixed(2)} прибыли`,
      tag: 'tp-hit',
    });
  }

  showStopLossHit(symbol, pnl) {
    return this.show('🛑 Stop Loss сработал', {
      body: `${symbol}: -$${Math.abs(pnl).toFixed(2)} убытка`,
      tag: 'sl-hit',
    });
  }

  showLiquidation(symbol, loss) {
    return this.show('⚠️ Ликвидация позиции', {
      body: `${symbol}: позиция ликвидирована. Убыток: $${Math.abs(loss).toFixed(2)}`,
      tag: 'liquidation',
      requireInteraction: true,
    });
  }

  showCustom(title, message, type = 'info') {
    const icons = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌',
      promo: '🎁',
    };
    return this.show(`${icons[type] || ''} ${title}`, {
      body: message,
      tag: type,
    });
  }
}

// Singleton instance
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;
